import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import Game from './components/Game';
import PrivyAuth from './util/PrivyAuth';
import BlossomEffect from './components/ui/Blossom';
import UserCheck from './util/UserCheck';
import AnimatedHero from './components/ui/AnimatedHero';
import { StyledButton } from './components/ui/StyledButton';
import { OnboardingModal, OnboardingForm } from './components/ui/OnboardingModal';
import { Store, DoorOpen, Trophy } from 'lucide-react';
import StorePage from './pages/StorePage';
import LeaderboardPage from './pages/LeaderboardPage';

const LandingPage = ({ onEnter }) => {
  const navigate = useNavigate();
  const [walletStatus, setWalletStatus] = useState('');
  const [userStatus, setUserStatus] = useState('');
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [sound, setSound] = useState(null);
  const [walletAddress, setWalletAddress] = useState('');
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    // const audio = new Audio('/audio/sfx/solagotchi.mp3'); 
    const audio = new Audio('/audio/sfx/binagotchi.mp3'); 
    setSound(audio);
    
    return () => {
      if (sound) {
        sound.pause();
        sound.currentTime = 0;
      }
    };
  }, []);

  const handleWalletConnect = useCallback((publicKey) => {
    setWalletAddress(publicKey);
    setIsWalletConnected(true);
  }, []);

  const handleDisconnect = useCallback(() => {
    setIsWalletConnected(false);
    setWalletStatus('');
    setUserStatus('');
    setWalletAddress('');
    setIsRegistered(false);
    setUserData(null);
  }, []);

  const handleUserFound = useCallback((user) => {
    if (user && user.username) {
      setUserStatus(`Welcome, ${user.username}!`);
      setIsRegistered(true);
      setUserData(user);
    } else {
      setIsRegistered(false);
      setUserData(null);
    }
  }, []);

  const handleEnterClick = useCallback(() => {
    
    if (!isRegistered) {
      setShowRegistrationModal(true);
      return;
    }
    
    if (sound) {
      sound.currentTime = 0;
      sound.volume = 0.25;
      sound.play();
    }
    onEnter(walletAddress, userData);
  }, [isRegistered, userData, walletAddress, sound, onEnter]);

  const handleEnterStore = useCallback(() => {
    navigate('/store');
  }, [navigate]);

  const handleEnterLeaderboard = useCallback(() => {
    navigate('/leaderboard');
  }, [navigate]);

  return (
    <div className="relative min-h-screen flex flex-col ">
      {/* Background Image */}
      <img
        src="/sprites/bg/cherry.gif"
        alt="Background"
        className="fixed inset-0 w-full h-full object-cover object-center -z-10"
      />
      {/* <div
        className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/sprites/bg/cherry.gif)',
          zIndex: -10
        }}
      /> */}
      <BlossomEffect/>
      <div className="h-[60vh] md:h-[70vh] flex justify-center">
        <AnimatedHero />
      </div>
      <div className="h-[40vh] md:h-[30vh] flex flex-col justify-start items-center pt-2">
        <div className="text-center w-full text-md font-bold text-white drop-shadow-[0_1.2px_1.2px_rgba(0,0,0,0.8)] mb-2">
          {walletStatus}
        </div>
        <div className="text-center w-full text-md font-bold text-white drop-shadow-[0_1.2px_1.2px_rgba(0,0,0,0.8)] mb-4">
          {userStatus}
        </div>
          <div className="flex justify-center items-center mt-4 gap-4">
            <PrivyAuth
              onConnect={handleWalletConnect}
              onStatusChange={setWalletStatus}
              onDisconnect={handleDisconnect}
            />
            {isWalletConnected && (
              <>
                <UserCheck 
                  key={walletAddress} // Add key to ensure proper remounting
                  walletAddress={walletAddress}
                  onUserFound={handleUserFound}
                  isRegistered={isRegistered} // Pass down registration state
                />
                <OnboardingModal 
                  isOpen={showRegistrationModal} 
                  onClose={() => setShowRegistrationModal(false)}
                >
                  <OnboardingForm 
                    walletAddress={walletAddress} 
                    onComplete={(user) => {
                      setShowRegistrationModal(false);
                      handleUserFound(user);
                    }} 
                  />
                </OnboardingModal>
              </>
            )}
            <StyledButton 
              onClick={handleEnterClick}
              disabled={!isWalletConnected}
              className="animate-bounce "
            >
              <div className="flex flex-center"><DoorOpen className="text-pink" /> <span className="ml-2 text-pink">进入 (Enter)</span></div>
            </StyledButton>
            <StyledButton 
              onClick={handleEnterStore}
              disabled={!isWalletConnected}
            >
                <div className="flex flex-center"><Store className="text-pinkAccent" /> <span className="ml-2 text-pinkAccent">店铺 (Store)</span></div>
            </StyledButton>
            <StyledButton 
              onClick={handleEnterLeaderboard}
              disabled={!isWalletConnected}
            >
                <div className="flex flex-center"><Store className="text-cyan-500" /> <span className="ml-2 text-cyan-500">排行榜 (Leaderboard)</span></div>
            </StyledButton>
            
        </div>
        
      </div>
    </div>
  );
};


function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/game" element={<GamePage />} />
        <Route path="/store" element={<StorePage />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
      </Routes>
    </BrowserRouter>
  );
}

function HomePage() {
  const navigate = useNavigate();

  const handleEnterGame = (walletAddress, userData) => {
    navigate('/game', { state: { walletAddress, userData } });
  };

  return <LandingPage onEnter={handleEnterGame} />;
}

function GamePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};

  const handleWalletChange = () => {
    navigate('/');
  };

  return (
    <Game
      onWalletChange={handleWalletChange}
      walletAddress={state.walletAddress}
      userData={state.userData}
    />
  );
}

export default App;